import React, { useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import LegacyFALogo from '../components/ui/LegacyFALogo';
import Icon from '../components/ui/Icon';
import Avatar from '../components/ui/Avatar';
import Btn from '../components/ui/Btn';

// ─── Constants ────────────────────────────────────────────
const SIGNING_TYPES   = ['Remote', 'In Person'];
const ROLES           = ['Recipient', 'CC', 'Witness'];
const CATEGORIES      = ['Insurance', 'Financial Planning', 'Trust & Estate', 'Legal', 'General'];
const EXPIRY_OPTIONS  = ['30 Days', '60 Days', '90 Days', '120 Days', 'Custom Days'];
const ALERT_OPTIONS   = ['7 Days', '14 Days', '30 Days', 'Custom Days'];
const ACCEPTED_TYPES  = '.jpeg,.jpg,.png,.pdf,.doc,.docx,.txt';

// ─── Design-system Stepper ───────────────────────────────
const STEPS = [
  { n: 1, label: 'Documents',  hint: 'Add files' },
  { n: 2, label: 'Recipients', hint: 'Signing order' },
  { n: 3, label: 'Message',    hint: 'Subject & schedule' },
];

const Stepper = ({ current }) => (
  <div className="bg-paper-100 ring-1 ring-ink-100 rounded-xl2 p-2">
    <div className="flex items-center">
      {STEPS.map((s, i) => {
        const done   = i + 1 < current;
        const active = i + 1 === current;
        return (
          <React.Fragment key={s.n}>
            <div className={`flex items-center gap-3 px-3 py-1.5 rounded-lg flex-1 ${active ? 'bg-white ring-1 ring-ink-100 shadow-soft' : ''}`}>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[12px] font-semibold tnum flex-shrink-0
                ${done ? 'bg-leaf-500 text-white' : active ? 'bg-ink-700 text-white' : 'bg-paper-200 text-ink-400 ring-1 ring-inset ring-ink-100'}`}>
                {done
                  ? <Icon name="check" size={13} strokeWidth={2.6} />
                  : s.n}
              </div>
              <div className="min-w-0">
                <div className={`text-[12.5px] font-semibold leading-tight ${active ? 'text-ink-900' : done ? 'text-ink-700' : 'text-ink-400'}`}>{s.label}</div>
                <div className="text-[10.5px] text-ink-400 truncate">{s.hint}</div>
              </div>
            </div>
            {i < STEPS.length - 1 && (
              <div className={`h-px flex-shrink-0 w-6 ${done ? 'bg-leaf-300' : 'bg-ink-100'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  </div>
);

// ─── Thin shared header (used by both Group + Self pages) ─
const PageHeader = ({ navigate, user, onLogout }) => (
  <header className="bg-paper-100 border-b border-ink-100 flex-shrink-0">
    <div className="h-16 px-6 flex items-center justify-between">
      <button onClick={() => navigate('/dashboard')} className="flex-shrink-0">
        <LegacyFALogo size="sm" />
      </button>
      <div className="flex items-center gap-3">
        <span className="text-[13px] text-ink-600 hidden sm:block">{user?.name || user?.email}</span>
        <Avatar name={user?.name || user?.email || '?'} size={30} />
        <button onClick={onLogout} className="p-2 rounded-lg text-ink-400 hover:text-ink-700 hover:bg-white/60" title="Sign out">
          <Icon name="logout" size={16} />
        </button>
      </div>
    </div>
  </header>
);

// ─── Toggle switch ────────────────────────────────────────
const Toggle = ({ on, onToggle, label }) => (
  <label className="flex items-center justify-between gap-3 py-3 border-t border-ink-50 cursor-pointer">
    <span className="text-[13px] text-ink-700">{label}</span>
    <button
      type="button"
      onClick={onToggle}
      className={`relative w-10 h-6 rounded-full transition-colors flex-shrink-0 ${on ? 'bg-leaf-500' : 'bg-ink-200'}`}
    >
      <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow-soft transition-transform ${on ? 'translate-x-[18px]' : 'translate-x-0.5'}`} />
    </button>
  </label>
);

// ─── Label + optional hint ────────────────────────────────
const FormField = ({ label, required, hint, children }) => (
  <div>
    <div className="flex items-baseline justify-between mb-1.5">
      <label className="text-[12px] font-semibold text-ink-700">
        {label}{required && <span className="text-flame-500 ml-0.5">*</span>}
      </label>
      {hint && <span className="text-[11px] text-ink-400">{hint}</span>}
    </div>
    {children}
  </div>
);

// ─── Main Component ──────────────────────────────────────
const CreateDocumentPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const signingType = searchParams.get('type') || 'group';
  const { user, logout } = useAuth();

  const [step,       setStep]       = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [error,      setError]      = useState('');

  // Step 1 – Files
  const [files,         setFiles]         = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [dragActive,    setDragActive]    = useState(false);
  const fileInputRef = useRef(null);

  // Step 2 – Recipients
  const [recipients, setRecipients] = useState([
    { id: `r_${Date.now()}`, role: 'Recipient', name: '', email: '', signing_type: 'Remote', sequence: 1, isSelf: false },
  ]);
  const [showPrivateMsg,     setShowPrivateMsg]     = useState(false);
  const [privateRecipientId, setPrivateRecipientId] = useState(null);
  const [privateMsgText,     setPrivateMsgText]     = useState('');

  // Step 3 – Message
  const [subject,          setSubject]          = useState('');
  const [message,          setMessage]          = useState('');
  const [category,         setCategory]         = useState('');
  const [autoReminders,    setAutoReminders]    = useState(true);
  const [expiryOption,     setExpiryOption]     = useState('120 Days');
  const [customExpiryDays, setCustomExpiryDays] = useState('');
  const [alertOption,      setAlertOption]      = useState('7 Days');
  const [customAlertDays,  setCustomAlertDays]  = useState('');

  const handleLogout = () => { logout(); navigate('/login'); };

  // ── File helpers ─────────────────────────────────────
  const handleFileDrop = (fileList) => {
    const newFiles = Array.from(fileList).map(f => ({
      id:   `file_${Date.now()}_${Math.random().toString(36).slice(2)}`,
      file: f,
      name: f.name,
      date: new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
    }));
    setFiles(prev => [...prev, ...newFiles]);
  };
  const handleDrag = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragActive(e.type === 'dragenter' || e.type === 'dragover');
  };
  const toggleFileSelect  = (id) => setSelectedFiles(p => p.includes(id) ? p.filter(i => i !== id) : [...p, id]);
  const deleteSelected    = ()   => { setFiles(p => p.filter(f => !selectedFiles.includes(f.id))); setSelectedFiles([]); };
  const removeFile        = (id) => { setFiles(p => p.filter(f => f.id !== id)); setSelectedFiles(p => p.filter(i => i !== id)); };

  // ── Recipient helpers ────────────────────────────────
  const addRecipient    = () => {
    const maxSeq = Math.max(0, ...recipients.map(r => r.sequence));
    setRecipients(p => [...p, { id: `r_${Date.now()}`, role: 'Recipient', name: '', email: '', signing_type: 'Remote', sequence: maxSeq + 1 }]);
  };
  const updateRecipient = (id, field, val) => setRecipients(p => p.map(r => r.id === id ? { ...r, [field]: val } : r));
  const removeRecipient = (id) => setRecipients(p => p.filter(r => r.id !== id));

  // ── Navigation ───────────────────────────────────────
  const canNext = () => {
    if (step === 1) return files.length > 0;
    if (step === 2) return recipients.length > 0 && recipients.every(r => r.name && r.email);
    if (step === 3) return subject.trim().length > 0;
    return true;
  };
  const next = () => { if (canNext()) setStep(s => Math.min(s + 1, 3)); };
  const prev = () => setStep(s => Math.max(s - 1, 1));

  // ── Submit ───────────────────────────────────────────
  const getExpiryDays = () => expiryOption === 'Custom Days' ? (parseInt(customExpiryDays) || 120) : (parseInt(expiryOption) || 120);

  const handleSubmit = async () => {
    setSubmitting(true); setError('');
    try {
      const pdfFiles = files.filter(f => f.file.type === 'application/pdf');
      if (pdfFiles.length === 0) { setError('Please upload at least one PDF file'); setSubmitting(false); return; }

      const recipientsList = signingType === 'self'
        ? [{ id: 'self', name: user?.name || '', email: user?.email || '', signing_order: 1 }]
        : [...recipients].sort((a, b) => a.sequence - b.sequence).map(r => ({
            id: r.id, name: r.name, email: r.email, signing_order: r.sequence,
            ...(r.password ? { signing_password: r.password } : {}),
          }));

      if (pdfFiles.length === 1) {
        const fd = new FormData();
        fd.append('pdf', pdfFiles[0].file);
        fd.append('title', subject || pdfFiles[0].name);
        fd.append('expires_in_days', getExpiryDays().toString());
        fd.append('email_message', message);
        recipientsList.forEach((r, i) => {
          fd.append(`recipients[${i}][id]`, r.id);
          fd.append(`recipients[${i}][name]`, r.name);
          fd.append(`recipients[${i}][email]`, r.email);
          fd.append(`recipients[${i}][signing_order]`, String(r.signing_order));
          if (r.signing_password) fd.append(`recipients[${i}][signing_password]`, r.signing_password);
        });
        const result = await api.createDocument(fd);
        const docId = result.document?.id || result.id;
        navigate(docId ? `/signing-space/${docId}` : '/dashboard');
      } else {
        const fd = new FormData();
        pdfFiles.forEach(f => fd.append('pdfs', f.file));
        fd.append('title', subject || 'Document Bundle');
        fd.append('document_titles', JSON.stringify(pdfFiles.map(f => f.name.replace(/\.pdf$/i, ''))));
        fd.append('expires_in_days', getExpiryDays().toString());
        fd.append('recipients', JSON.stringify(recipientsList));
        const result = await api.createBundle(fd);
        const bundleId = result.bundle?.bundle_id || result.bundle_id;
        navigate(bundleId ? `/signing-space/bundle/${bundleId}` : '/dashboard');
      }
    } catch (err) {
      setError(err.message || 'Failed to create document');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Self Sign ────────────────────────────────────────
  if (signingType === 'self') {
    return (
      <div className="min-h-screen bg-canvas flex flex-col">
        <PageHeader navigate={navigate} user={user} onLogout={handleLogout} />
        <main className="flex-1 px-6 lg:px-8 py-6 max-w-2xl mx-auto w-full">
          <div className="mb-4">
            <button onClick={() => navigate('/dashboard')} className="text-[12px] text-ink-400 hover:text-ink-700 inline-flex items-center gap-1">
              <Icon name="arrowLeft" size={12} /> All envelopes
            </button>
            <h1 className="font-display text-[26px] font-bold text-ink-900 mt-1">Self signing</h1>
          </div>

          <div className="bg-paper-100 rounded-2xl ring-1 ring-ink-100 shadow-soft">
            <div className="px-6 pt-6 pb-4 border-b border-ink-100">
              <h2 className="font-display text-[18px] font-semibold text-ink-900">Upload documents to sign</h2>
              <p className="text-[12.5px] text-ink-500 mt-0.5">Add one or more PDFs — you'll sign them all in one session.</p>
            </div>

            <div className="p-6">
              {/* Drop zone */}
              <div
                className={`rounded-xl2 ring-1 ring-dashed p-8 flex flex-col items-center justify-center text-center cursor-pointer transition-colors ${
                  dragActive ? 'ring-leaf-400 bg-leaf-50' : 'ring-ink-200 bg-paper-200/60 hover:ring-ink-300'
                }`}
                onDragEnter={handleDrag} onDragOver={handleDrag} onDragLeave={handleDrag}
                onDrop={e => { e.preventDefault(); setDragActive(false); handleFileDrop(e.dataTransfer.files); }}
                onClick={() => fileInputRef.current?.click()}
              >
                <input ref={fileInputRef} type="file" accept={ACCEPTED_TYPES} multiple onChange={e => handleFileDrop(e.target.files)} className="hidden" />
                <div className="w-14 h-14 rounded-2xl bg-leaf-50 ring-1 ring-leaf-100 flex items-center justify-center text-leaf-600 mb-3">
                  <Icon name="upload" size={22} />
                </div>
                <div className="text-[14px] font-semibold text-ink-900">
                  Drag documents here or <span className="text-leaf-700">browse</span>
                </div>
                <div className="text-[11.5px] text-ink-400 mt-1">PDF, DOCX, JPG, PNG · Up to 200 MB</div>
              </div>

              {/* Error */}
              {error && (
                <div className="mt-4 flex items-center gap-2.5 p-3.5 bg-flame-50 ring-1 ring-flame-100 rounded-xl text-flame-600 text-[13px]">
                  <Icon name="alert" size={15} /> {error}
                </div>
              )}

              {/* File list */}
              {files.length > 0 && (
                <div className="mt-4 space-y-2">
                  {files.map(f => (
                    <div key={f.id} className="flex items-center gap-3 p-3 rounded-xl bg-white ring-1 ring-ink-100">
                      <input type="checkbox" checked={selectedFiles.includes(f.id)} onChange={() => toggleFileSelect(f.id)}
                        className="rounded border-ink-200 text-leaf-600 focus:ring-leaf-500" />
                      <div className="w-10 h-12 rounded bg-flame-50 flex flex-col items-center justify-center flex-shrink-0">
                        <span className="text-[8px] font-bold text-flame-600 tracking-wider">
                          {f.file.type === 'application/pdf' ? 'PDF' : f.file.name.split('.').pop().toUpperCase()}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-semibold text-ink-900 truncate">{f.name}</div>
                        <div className="text-[11px] text-ink-400 tnum">{f.date}</div>
                      </div>
                      <div className="flex items-center gap-1 text-ink-400">
                        <button className="p-1.5 rounded hover:bg-paper-200 hover:text-ink-800"><Icon name="refresh" size={14} /></button>
                        <button className="p-1.5 rounded hover:bg-paper-200 hover:text-ink-800"><Icon name="eye" size={14} /></button>
                        <button onClick={() => removeFile(f.id)} className="p-1.5 rounded hover:bg-flame-50 hover:text-flame-600"><Icon name="trash" size={14} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-ink-100 flex items-center justify-between">
              {selectedFiles.length > 0
                ? <button onClick={deleteSelected} className="text-[13px] text-flame-600 font-medium hover:text-flame-700">Delete selected ({selectedFiles.length})</button>
                : <div />}
              <div className="flex gap-2">
                <Btn tone="outline" onClick={() => navigate('/dashboard')}>Cancel</Btn>
                <Btn disabled={files.length === 0 || submitting} onClick={handleSubmit}>
                  {submitting ? 'Processing…' : 'Sign documents'}
                </Btn>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // ── Group Sign – 3-step wizard ───────────────────────
  return (
    <div className="min-h-screen bg-canvas flex flex-col">
      <PageHeader navigate={navigate} user={user} onLogout={handleLogout} />

      <main className="flex-1 px-6 lg:px-8 py-6">
        <div className="max-w-[1200px] mx-auto flex flex-col gap-4 min-h-0">

          {/* Page title */}
          <div className="flex items-start justify-between">
            <div>
              <button onClick={() => navigate('/dashboard')} className="text-[12px] text-ink-400 hover:text-ink-700 inline-flex items-center gap-1">
                <Icon name="arrowLeft" size={12} /> All envelopes
              </button>
              <h1 className="font-display text-[26px] font-bold text-ink-900 mt-1">New group signing</h1>
            </div>
            <button onClick={() => navigate('/dashboard')} className="text-[12px] text-ink-500 hover:text-ink-800 mt-1">
              Save & close
            </button>
          </div>

          {/* Stepper */}
          <Stepper current={step} />

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2.5 p-3.5 bg-flame-50 ring-1 ring-flame-100 rounded-xl text-flame-600 text-[13px]">
              <Icon name="alert" size={15} /> {error}
            </div>
          )}

          {/* Step panels */}
          {step === 1 && (
            <StepDocuments
              files={files} selectedFiles={selectedFiles} dragActive={dragActive}
              fileInputRef={fileInputRef} handleDrag={handleDrag} handleFileDrop={handleFileDrop}
              toggleFileSelect={toggleFileSelect} deleteSelected={deleteSelected} removeFile={removeFile}
              onBack={() => navigate('/dashboard')} onNext={next} canNext={canNext()}
            />
          )}

          {step === 2 && (
            <StepRecipients
              recipients={recipients} updateRecipient={updateRecipient}
              addRecipient={addRecipient} removeRecipient={removeRecipient}
              setPrivateRecipientId={setPrivateRecipientId} setPrivateMsgText={setPrivateMsgText}
              setShowPrivateMsg={setShowPrivateMsg} user={user}
              onBack={prev} onNext={next} canNext={canNext()}
            />
          )}

          {step === 3 && (
            <StepMessage
              subject={subject} setSubject={setSubject} message={message} setMessage={setMessage}
              category={category} setCategory={setCategory} autoReminders={autoReminders} setAutoReminders={setAutoReminders}
              expiryOption={expiryOption} setExpiryOption={setExpiryOption}
              customExpiryDays={customExpiryDays} setCustomExpiryDays={setCustomExpiryDays}
              alertOption={alertOption} setAlertOption={setAlertOption}
              customAlertDays={customAlertDays} setCustomAlertDays={setCustomAlertDays}
              onBack={prev} onSubmit={handleSubmit} submitting={submitting} canSubmit={canNext()}
            />
          )}
        </div>
      </main>

      {/* Private message modal */}
      {showPrivateMsg && (
        <PrivateMessageModal
          message={privateMsgText}
          setMessage={setPrivateMsgText}
          onSave={() => {
            if (privateRecipientId) updateRecipient(privateRecipientId, 'privateMessage', privateMsgText);
            setShowPrivateMsg(false); setPrivateMsgText(''); setPrivateRecipientId(null);
          }}
          onCancel={() => { setShowPrivateMsg(false); setPrivateMsgText(''); setPrivateRecipientId(null); }}
        />
      )}
    </div>
  );
};

// ─── Step 1: Documents ────────────────────────────────────
const StepDocuments = ({ files, selectedFiles, dragActive, fileInputRef, handleDrag, handleFileDrop, toggleFileSelect, deleteSelected, removeFile, onBack, onNext, canNext }) => (
  <div className="grid grid-cols-[1fr_320px] gap-5">
    {/* Main panel */}
    <div className="bg-paper-100 rounded-2xl ring-1 ring-ink-100 shadow-soft p-6 flex flex-col">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <h2 className="font-display font-semibold text-ink-900 text-[18px]">Documents in this envelope</h2>
          <p className="text-[12.5px] text-ink-500">Add one or more files. They'll be signed as a single bundle.</p>
        </div>
        {files.length > 0 && <span className="text-[11px] text-ink-400 tnum">{files.length} file{files.length !== 1 ? 's' : ''}</span>}
      </div>

      {/* Drop zone */}
      <div
        className={`rounded-xl2 ring-1 ring-dashed p-8 flex flex-col items-center justify-center text-center cursor-pointer transition-colors ${
          dragActive ? 'ring-leaf-400 bg-leaf-50' : 'ring-ink-200 bg-paper-200/60 hover:ring-ink-300'
        }`}
        onDragEnter={handleDrag} onDragOver={handleDrag} onDragLeave={handleDrag}
        onDrop={e => { e.preventDefault(); handleFileDrop(e.dataTransfer.files); }}
        onClick={() => fileInputRef.current?.click()}
      >
        <input ref={fileInputRef} type="file" accept={ACCEPTED_TYPES} multiple onChange={e => handleFileDrop(e.target.files)} className="hidden" />
        <div className="w-14 h-14 rounded-2xl bg-leaf-50 ring-1 ring-leaf-100 flex items-center justify-center text-leaf-600 mb-3">
          <Icon name="upload" size={22} />
        </div>
        <div className="text-[14px] font-semibold text-ink-900">Drag documents here or <span className="text-leaf-700">browse</span></div>
        <div className="text-[11.5px] text-ink-400 mt-1">PDF, DOCX, JPG, PNG · Up to 200 MB · Max 25 files</div>
      </div>

      {/* Files */}
      {files.length > 0 && (
        <div className="mt-5 space-y-2 flex-1 overflow-auto thin-scroll">
          {files.map(f => (
            <div key={f.id} className="flex items-center gap-3 p-3 rounded-xl bg-white ring-1 ring-ink-100 hover:ring-ink-200 transition-colors">
              <input type="checkbox" checked={selectedFiles.includes(f.id)} onChange={() => toggleFileSelect(f.id)}
                className="rounded border-ink-200 text-leaf-600 focus:ring-leaf-500" />
              <div className="cursor-grab text-ink-300"><Icon name="grip" size={14} /></div>
              <div className="w-10 h-12 rounded bg-flame-50 flex flex-col items-center justify-center flex-shrink-0">
                <span className="text-[8px] font-bold text-flame-600 tracking-wider">
                  {f.file.type === 'application/pdf' ? 'PDF' : f.file.name.split('.').pop().toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-semibold text-ink-900 truncate">{f.name}</div>
                <div className="text-[11px] text-ink-400 tnum">Added {f.date}</div>
              </div>
              <div className="flex items-center gap-1 text-ink-400">
                <button className="p-1.5 rounded hover:bg-paper-200 hover:text-ink-800"><Icon name="eye" size={14} /></button>
                <button className="p-1.5 rounded hover:bg-paper-200 hover:text-ink-800"><Icon name="refresh" size={14} /></button>
                <button onClick={() => removeFile(f.id)} className="p-1.5 rounded hover:bg-flame-50 hover:text-flame-600"><Icon name="trash" size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-4 pt-4 border-t border-ink-100 flex items-center justify-between">
        {selectedFiles.length > 0
          ? <button onClick={deleteSelected} className="text-[13px] text-flame-600 font-medium">Delete selected ({selectedFiles.length})</button>
          : <button onClick={() => fileInputRef.current?.click()} className="text-[13px] text-leaf-700 font-medium">+ Add more files</button>}
        <div className="flex gap-2">
          <Btn tone="outline" onClick={onBack}>Cancel</Btn>
          <Btn trailing="arrowRight" disabled={!canNext} onClick={onNext}>Continue to recipients</Btn>
        </div>
      </div>
    </div>

    {/* Right tips panel */}
    <div className="space-y-4">
      <div className="bg-ink-700 text-white rounded-2xl p-5">
        <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.18em] text-leaf-300 mb-3">
          <Icon name="sparkle" size={12} /> Bundled signing
        </div>
        <div className="font-display text-[16px] font-semibold leading-snug mb-2">Sign multiple documents in one flow.</div>
        <div className="text-[12.5px] text-white/70 leading-relaxed">Recipients sign the full bundle in a single session — with one audit trail and one envelope ID.</div>
      </div>
      <div className="bg-paper-100 rounded-2xl ring-1 ring-ink-100 p-4">
        <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-ink-400 mb-2">Good to know</div>
        <ul className="space-y-1.5">
          {[
            { label: 'PDFs with fillable fields work best', ok: true },
            { label: 'Scanned forms are auto-OCRed', ok: true },
            { label: "Password-protected PDFs aren't supported", ok: false },
          ].map((it, i) => (
            <li key={i} className="flex items-start gap-2 text-[12.5px] text-ink-600">
              <span className={`mt-0.5 w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${it.ok ? 'bg-leaf-100 text-leaf-700' : 'bg-amber-100 text-amber-600'}`}>
                {it.ok ? <Icon name="check" size={10} strokeWidth={3} /> : <Icon name="x" size={10} strokeWidth={3} />}
              </span>
              {it.label}
            </li>
          ))}
        </ul>
      </div>
    </div>
  </div>
);

// ─── Step 2: Recipients ───────────────────────────────────
const StepRecipients = ({ recipients, updateRecipient, addRecipient, removeRecipient, setPrivateRecipientId, setPrivateMsgText, setShowPrivateMsg, user, onBack, onNext, canNext }) => {
  const [draftOrders, setDraftOrders] = React.useState(() =>
    Object.fromEntries(recipients.map(r => [r.id, String(r.sequence)])));

  React.useEffect(() => {
    setDraftOrders(prev => {
      const next = { ...prev };
      recipients.forEach(r => { if (!(r.id in next)) next[r.id] = String(r.sequence); });
      return next;
    });
  }, [recipients]);

  const commitOrder = (id) => {
    const raw = draftOrders[id]; const parsed = parseInt(raw, 10);
    if (!raw || isNaN(parsed) || parsed < 1) {
      const r = recipients.find(r => r.id === id);
      setDraftOrders(p => ({ ...p, [id]: String(r?.sequence ?? 1) }));
    } else { updateRecipient(id, 'sequence', parsed); }
  };

  const sorted = [...recipients].sort((a, b) => a.sequence - b.sequence);

  return (
    <div className="grid grid-cols-[1fr_320px] gap-5">
      {/* Main panel */}
      <div className="bg-paper-100 rounded-2xl ring-1 ring-ink-100 shadow-soft p-6 flex flex-col">
        <div className="flex items-baseline justify-between mb-4">
          <div>
            <h2 className="font-display font-semibold text-ink-900 text-[18px]">Who's signing?</h2>
            <p className="text-[12.5px] text-ink-500">Set signing order. Same-number recipients sign in parallel.</p>
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto thin-scroll">
          <table className="w-full">
            <thead>
              <tr className="border-b border-ink-100">
                {['Order','Role','Full name','Email','Type',''].map((h, i) => (
                  <th key={i} className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-ink-400">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map(r => (
                <tr key={r.id} className="border-b border-ink-50 hover:bg-white/60 transition-colors">
                  <td className="px-3 py-3">
                    <input
                      type="text" inputMode="numeric" pattern="[0-9]*"
                      value={draftOrders[r.id] ?? r.sequence}
                      onChange={e => setDraftOrders(p => ({ ...p, [r.id]: e.target.value.replace(/\D/g, '') }))}
                      onBlur={() => commitOrder(r.id)}
                      onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
                      className="w-12 h-8 px-2 rounded-lg bg-white ring-1 ring-ink-200 text-[13px] text-center outline-none focus:ring-leaf-500 tnum"
                    />
                  </td>
                  <td className="px-3 py-3">
                    <select value={r.role} onChange={e => updateRecipient(r.id, 'role', e.target.value)}
                      className="h-8 px-2 rounded-lg bg-white ring-1 ring-ink-200 text-[13px] outline-none focus:ring-leaf-500 w-full">
                      {ROLES.map(ro => <option key={ro} value={ro}>{ro}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-3">
                    <input type="text" value={r.name} onChange={e => updateRecipient(r.id, 'name', e.target.value)}
                      placeholder="Full name" readOnly={r.isSelf}
                      className="w-full h-8 px-2.5 rounded-lg bg-white ring-1 ring-ink-200 text-[13px] outline-none focus:ring-leaf-500" />
                  </td>
                  <td className="px-3 py-3">
                    <input type="email" value={r.email} onChange={e => updateRecipient(r.id, 'email', e.target.value)}
                      placeholder="Email" readOnly={r.isSelf || r.signing_type === 'In Person'}
                      className="w-full h-8 px-2.5 rounded-lg bg-white ring-1 ring-ink-200 text-[13px] outline-none focus:ring-leaf-500" />
                  </td>
                  <td className="px-3 py-3">
                    {r.isSelf ? (
                      <span className="text-[12px] text-ink-500 px-2">Self</span>
                    ) : (
                      <select value={r.signing_type}
                        onChange={e => {
                          updateRecipient(r.id, 'signing_type', e.target.value);
                          if (e.target.value === 'In Person') updateRecipient(r.id, 'email', user?.email || '');
                        }}
                        className="h-8 px-2 rounded-lg bg-white ring-1 ring-ink-200 text-[13px] outline-none focus:ring-leaf-500 w-full">
                        {SIGNING_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => updateRecipient(r.id, 'password', prompt('Set signing password for ' + (r.name || 'recipient') + ':') || '')}
                        className={`w-7 h-7 rounded-md flex items-center justify-center transition-colors ${r.password ? 'bg-leaf-500 text-white' : 'text-ink-400 hover:text-ink-800 hover:bg-paper-200'}`}
                        title="Set password">
                        <Icon name="key" size={13} />
                      </button>
                      <button
                        onClick={() => { setPrivateRecipientId(r.id); setPrivateMsgText(r.privateMessage || ''); setShowPrivateMsg(true); }}
                        className={`w-7 h-7 rounded-md flex items-center justify-center transition-colors ${r.privateMessage ? 'bg-leaf-500 text-white' : 'text-ink-400 hover:text-ink-800 hover:bg-paper-200'}`}
                        title="Private note">
                        <Icon name="mail" size={13} />
                      </button>
                      {!r.isSelf && (
                        <button onClick={() => removeRecipient(r.id)}
                          className="w-7 h-7 rounded-md flex items-center justify-center text-ink-400 hover:text-flame-600 hover:bg-flame-50 transition-colors">
                          <Icon name="trash" size={13} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-4 pt-4 border-t border-ink-100 flex items-center justify-between">
          <button onClick={addRecipient} className="text-[13px] text-leaf-700 font-medium hover:text-leaf-800">+ Add recipient</button>
          <div className="flex gap-2">
            <Btn tone="outline" icon="arrowLeft" onClick={onBack}>Back</Btn>
            <Btn trailing="arrowRight" disabled={!canNext} onClick={onNext}>Continue to message</Btn>
          </div>
        </div>
      </div>

      {/* Right panel */}
      <div className="space-y-4">
        <div className="bg-paper-100 rounded-2xl ring-1 ring-ink-100 p-4">
          <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-leaf-700 mb-2">Routing rules</div>
          <ul className="space-y-2 text-[12.5px] text-ink-600 leading-relaxed">
            <li className="flex gap-2"><span className="w-5 h-5 rounded-full bg-leaf-100 text-leaf-700 flex items-center justify-center flex-shrink-0 text-[10px] font-bold tnum">1</span>Same order number → signs in parallel</li>
            <li className="flex gap-2"><span className="w-5 h-5 rounded-full bg-leaf-100 text-leaf-700 flex items-center justify-center flex-shrink-0 text-[10px] font-bold tnum">2</span>Different orders → sequential, notified in turn</li>
            <li className="flex gap-2"><span className="w-5 h-5 rounded-full bg-leaf-100 text-leaf-700 flex items-center justify-center flex-shrink-0 text-[10px] font-bold tnum">3</span>CC recipients receive a copy on completion</li>
          </ul>
        </div>
        <div className="bg-leaf-50 ring-1 ring-leaf-200 rounded-2xl p-4 flex items-start gap-2">
          <div className="w-7 h-7 rounded-lg bg-white flex items-center justify-center text-leaf-700 flex-shrink-0"><Icon name="shield" size={14} /></div>
          <div>
            <div className="text-[12.5px] font-semibold text-leaf-800">Secure signing</div>
            <div className="text-[11.5px] text-leaf-700 mt-0.5">Add per-recipient passwords for regulated documents requiring extra verification.</div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Step 3: Message ─────────────────────────────────────
const StepMessage = ({ subject, setSubject, message, setMessage, category, setCategory,
  autoReminders, setAutoReminders, expiryOption, setExpiryOption, customExpiryDays, setCustomExpiryDays,
  alertOption, setAlertOption, customAlertDays, setCustomAlertDays, onBack, onSubmit, submitting, canSubmit }) => (
  <div className="grid grid-cols-[1fr_380px] gap-5">
    {/* Main form */}
    <div className="bg-paper-100 rounded-2xl ring-1 ring-ink-100 shadow-soft p-6 overflow-auto thin-scroll">
      <h2 className="font-display font-semibold text-ink-900 text-[18px]">Message & delivery</h2>
      <p className="text-[12.5px] text-ink-500 mb-6">This is what recipients see in their signing invitation email.</p>

      <div className="space-y-5">
        <FormField label="Envelope subject" required help="Shown as the email subject line">
          <input type="text" value={subject} onChange={e => setSubject(e.target.value)}
            placeholder="e.g. Please sign: Chen Family Trust — Policy Amendment"
            className="w-full h-11 px-3.5 rounded-lg bg-white ring-1 ring-inset ring-ink-200 focus:ring-2 focus:ring-leaf-500 outline-none text-[14px] text-ink-800" />
        </FormField>

        <FormField label="Message to recipients" hint="Plain text · 1,500 chars max">
          <textarea rows={5} value={message} onChange={e => setMessage(e.target.value)}
            placeholder={`Dear client,\n\nPlease review and sign the attached documents.\n\nRegards,\n${''}`}
            className="w-full px-3.5 py-2.5 rounded-lg bg-white ring-1 ring-inset ring-ink-200 focus:ring-2 focus:ring-leaf-500 outline-none text-[13.5px] text-ink-800 resize-none leading-relaxed" />
        </FormField>

        <FormField label="Category">
          <div className="flex flex-wrap gap-1.5">
            {CATEGORIES.map(c => (
              <button key={c} type="button" onClick={() => setCategory(c)}
                className={`h-8 px-3 rounded-full text-[12px] font-medium transition-colors ${
                  category === c ? 'bg-ink-700 text-white' : 'bg-white text-ink-600 ring-1 ring-ink-200 hover:ring-ink-300'
                }`}>{c}</button>
            ))}
          </div>
        </FormField>

        <div className="h-px bg-ink-100" />

        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-ink-400 mb-3">Schedule & reminders</div>
          <div className="grid grid-cols-2 gap-4 mb-2">
            <FormField label="Envelope expires in">
              <select value={expiryOption} onChange={e => setExpiryOption(e.target.value)}
                className="w-full h-10 px-3 rounded-lg bg-white ring-1 ring-ink-200 text-[13px] outline-none focus:ring-leaf-500">
                {EXPIRY_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
              {expiryOption === 'Custom Days' && (
                <input type="number" value={customExpiryDays} onChange={e => setCustomExpiryDays(e.target.value)}
                  placeholder="Number of days" className="mt-2 w-full h-10 px-3 rounded-lg bg-white ring-1 ring-ink-200 text-[13px] outline-none focus:ring-leaf-500" />
              )}
            </FormField>
            <FormField label="Send reminder after">
              <select value={alertOption} onChange={e => setAlertOption(e.target.value)}
                className="w-full h-10 px-3 rounded-lg bg-white ring-1 ring-ink-200 text-[13px] outline-none focus:ring-leaf-500">
                {ALERT_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
              {alertOption === 'Custom Days' && (
                <input type="number" value={customAlertDays} onChange={e => setCustomAlertDays(e.target.value)}
                  placeholder="Number of days" className="mt-2 w-full h-10 px-3 rounded-lg bg-white ring-1 ring-ink-200 text-[13px] outline-none focus:ring-leaf-500" />
              )}
            </FormField>
          </div>
          <Toggle on={autoReminders} onToggle={() => setAutoReminders(!autoReminders)} label="Auto-remind every 7 days until signed" />
        </div>
      </div>

      <div className="mt-8 pt-5 border-t border-ink-100 flex items-center justify-between">
        <Btn tone="outline" icon="arrowLeft" onClick={onBack}>Back</Btn>
        <div className="flex gap-2">
          <Btn tone="ghost">Save as draft</Btn>
          <Btn trailing="arrowRight" onClick={onSubmit} disabled={!canSubmit || submitting}>
            {submitting ? 'Processing…' : 'Place signature fields'}
          </Btn>
        </div>
      </div>
    </div>

    {/* Right — email preview */}
    <div className="flex flex-col gap-4">
      <div className="bg-paper-100 rounded-2xl ring-1 ring-ink-100 p-5 flex-1 overflow-auto thin-scroll">
        <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-ink-400 mb-3">Email preview</div>
        <div className="bg-white rounded-xl ring-1 ring-ink-100 overflow-hidden">
          <div className="px-4 py-3 border-b border-ink-100 bg-paper-200/50 flex items-center gap-2">
            <span className="text-[11px] text-ink-500">From: advisory@legacyfa.com</span>
          </div>
          <div className="p-5">
            <div className="font-display text-[17px] font-semibold text-ink-900 leading-snug">
              {subject || 'Please sign: [your subject here]'}
            </div>
            <div className="text-[11px] text-ink-400 mt-1 tnum">Expires in {expiryOption.replace(' Days', 'd')}</div>
            <div className="my-4 h-px bg-ink-100" />
            <div className="text-[12.5px] text-ink-600 leading-relaxed whitespace-pre-line">
              {message || 'Your message will appear here.'}
            </div>
            <div className="mt-5">
              <div className="inline-flex items-center gap-2 h-10 px-4 bg-leaf-600 text-white rounded-lg text-[13px] font-semibold">
                Review and sign <Icon name="arrowRight" size={14} />
              </div>
            </div>
            <div className="mt-5 p-3 rounded-lg bg-paper-200/60 flex items-center gap-2">
              <Icon name="shield" size={15} className="text-leaf-700 flex-shrink-0" />
              <div className="text-[11px] text-ink-500 leading-snug">Secured by LegacyFA. Audit trail attached on completion.</div>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-ink-700 text-white rounded-2xl p-4 flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0"><Icon name="clock" size={15} /></div>
        <div className="text-[12px] leading-snug">
          First reminder fires <span className="font-semibold">{alertOption}</span> after send if unsigned.
          Envelope voids automatically after <span className="font-semibold tnum">{expiryOption}</span>.
        </div>
      </div>
    </div>
  </div>
);

// ─── Private Message Modal ────────────────────────────────
const PrivateMessageModal = ({ message, setMessage, onSave, onCancel }) => (
  <div className="fixed inset-0 bg-ink-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-2xl shadow-float ring-1 ring-ink-100 max-w-lg w-full">
      <div className="px-6 py-4 border-b border-ink-100 flex items-center justify-between">
        <div className="font-display text-[17px] font-semibold text-ink-900">Private note</div>
        <button onClick={onCancel} className="p-1.5 rounded-lg text-ink-400 hover:text-ink-700 hover:bg-paper-200"><Icon name="x" size={16} /></button>
      </div>
      <div className="px-6 py-5">
        <p className="text-[13px] text-ink-500 mb-3">Include a personal note visible only to this recipient.</p>
        <textarea
          value={message} onChange={e => setMessage(e.target.value)}
          placeholder="Enter a private message…" rows={4}
          className="w-full px-3.5 py-2.5 rounded-lg bg-white ring-1 ring-inset ring-ink-200 focus:ring-2 focus:ring-leaf-500 outline-none text-[13.5px] text-ink-800 resize-none"
        />
      </div>
      <div className="px-6 py-4 bg-paper-200/50 rounded-b-2xl flex justify-end gap-2">
        <Btn tone="outline" onClick={onCancel}>Cancel</Btn>
        <Btn onClick={onSave}>Save note</Btn>
      </div>
    </div>
  </div>
);

export default CreateDocumentPage;
